import { useFrame } from '@react-three/fiber';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import {
  Group,
  Mesh,
  PerspectiveCamera,
  ShaderMaterial,
  Texture,
  Uniform,
} from 'three';

import { Leaves } from '0_canvas/Leaves/Leaves';
import type { GroupProps } from 'types/r3f';

import {
  scaleBackground,
  WEATHER_SCREEN_PREFIX,
} from '../WeatherScreens.utils';
import fragmentShader from './shader.frag';
import vertexShader from './shader.vert';

interface WindyScreenProps extends GroupProps {
  backgroundTexture: Texture;
}

const WindyScreen = forwardRef<Group, WindyScreenProps>(
  ({ backgroundTexture, ...props }, ref) => {
    const backgroundRef = useRef<Mesh>(null!);
    const screenRef = useRef<Group>(null!);

    const materialRef = useRef<ShaderMaterial>(
      new ShaderMaterial({
        vertexShader,
        fragmentShader,
        uniforms: {
          uBackground: new Uniform(backgroundTexture),
        },
      })
    );

    useFrame(({ camera, size }) => {
      if (!screenRef.current || !backgroundRef.current || !backgroundTexture)
        return;

      scaleBackground({
        mesh: backgroundRef.current,
        camera: camera as PerspectiveCamera,
        size,
        texture: backgroundTexture,
        positionZ: screenRef.current.position.z,
      });

      materialRef.current.uniforms.uBackground = new Uniform(backgroundTexture);
    });

    useImperativeHandle(ref, () => screenRef.current);

    return (
      <group name={WEATHER_SCREEN_PREFIX + 'windy'} ref={screenRef} {...props}>
        <Leaves position={[0, 0, -419]} />
        <mesh ref={backgroundRef} rotation={[Math.PI, 0, 0]}>
          <planeGeometry args={[1, 1]} />
          <primitive object={materialRef.current} attach="material" />
        </mesh>
      </group>
    );
  }
);

WindyScreen.displayName = 'WindyScreen';
export default WindyScreen;
